<!-- Hidden file input for the verification proof upload -->
<input
  #proofInput
  type="file"
  hidden
  accept="image/*,application/pdf"
  (change)="onProofSelected($event)"
/>

<!-- Page head -->
<div class="page-head">
  <div>
    <div class="page-head__title">
      {{ greetingKey | transloco : { name: firstName } }}
    </div>
    <div class="page-head__sub">
      {{ "stats.periodSubtitle" | transloco : { days: days() } }}
    </div>
  </div>
</div>

<!-- Usage-tips carousel: one hint at a time, auto-rotating -->
<div
  class="hint-bar"
  *ngIf="!hintsHidden()"
  (mouseenter)="pauseHints()"
  (mouseleave)="resumeHints()"
>
  <button
    class="hint-bar__arrow"
    type="button"
    (click)="prevHint()"
    [attr.aria-label]="'stats.hints.prev' | transloco"
  >
    <mat-icon fontSet="material-icons-round">chevron_left</mat-icon>
  </button>

  <div class="hint-bar__icon">
    <mat-icon fontSet="material-icons-round">{{ activeHint().icon }}</mat-icon>
  </div>

  <div class="hint-bar__body">
    <div class="hint-bar__title">{{ activeHint().titleKey | transloco }}</div>
    <div class="hint-bar__text">{{ activeHint().textKey | transloco }}</div>
  </div>

  <app-button
    class="hint-bar__cta"
    [text]="activeHint().ctaKey | transloco"
    buttonType="secondary"
    [isBtnSmall]="true"
    (click)="openHintAction()"
  ></app-button>

  <button
    class="hint-bar__arrow"
    type="button"
    (click)="nextHint()"
    [attr.aria-label]="'stats.hints.next' | transloco"
  >
    <mat-icon fontSet="material-icons-round">chevron_right</mat-icon>
  </button>

  <button
    class="hint-bar__close"
    type="button"
    (click)="closeHints()"
    [attr.aria-label]="'stats.hints.close' | transloco"
  >
    <mat-icon fontSet="material-icons-round">close</mat-icon>
  </button>
</div>

<!-- Verification banner -->
<ng-container *ngIf="verification() as v">
  <div
    class="note note--warn company-statistics__note"
    *ngIf="v.status === 'PENDING'"
  >
    <mat-icon fontSet="material-icons-round">pending_actions</mat-icon>
    <div class="company-statistics__note-text">
      <strong>{{ "stats.verificationPendingTitle" | transloco }}</strong>
      {{ "stats.verificationPendingText" | transloco }}
    </div>
    <app-button
      *ngIf="!v.proofUploaded"
      [text]="'stats.attachProof' | transloco"
      buttonType="secondary"
      [isBtnSmall]="true"
      [disabled]="uploadingProof()"
      (click)="openProofPicker(proofInput)"
    ></app-button>
    <span *ngIf="v.proofUploaded" class="chip chip--warn">
      {{ "stats.proofReceived" | transloco }}
    </span>
  </div>

  <div
    class="note note--warn company-statistics__note"
    *ngIf="v.status === 'REJECTED'"
  >
    <mat-icon fontSet="material-icons-round">error_outline</mat-icon>
    <div class="company-statistics__note-text">
      <strong>{{ "stats.verificationRejectedTitle" | transloco }}</strong>
      {{ v.rejectReason || ("stats.verificationRejectedText" | transloco) }}
    </div>
    <app-button
      [text]="'stats.attachNewProof' | transloco"
      buttonType="secondary"
      [isBtnSmall]="true"
      [disabled]="uploadingProof()"
      (click)="openProofPicker(proofInput)"
    ></app-button>
  </div>
</ng-container>

<!-- Getting-started banner. The final step ('fullVersion') presents the trial
     notice + upgrade CTA; all earlier setup steps keep the "Nastavi" flow. -->
<div class="start-banner" *ngIf="showStartBanner()">
  <!-- Full-version upgrade submitted — all steps done, awaiting approval -->
  <ng-container *ngIf="upgradePending(); else onboardingBanner">
    <div class="start-banner__icon">
      <mat-icon fontSet="material-icons-round">hourglass_top</mat-icon>
    </div>
    <div class="start-banner__body">
      <h4>{{ "backoffice.waitingListTitle" | transloco }}</h4>
      <p>{{ "backoffice.waitingListMessage" | transloco }}</p>
    </div>
    <div class="start-banner__progress">
      <div class="start-banner__bar">
        <div
          class="start-banner__bar-fill"
          [style.width.%]="progressPct()"
        ></div>
      </div>
      <span>{{
        "stats.startProgress" | transloco : { done: completedCount(), total: totalSteps }
      }}</span>
      <span class="chip chip--warn">{{ "stats.awaitingApproval" | transloco }}</span>
    </div>
  </ng-container>

  <ng-template #onboardingBanner>
  <ng-container
    *ngIf="remainingSteps()[0] === 'fullVersion'; else setupBanner"
  >
    <div class="start-banner__icon">
      <mat-icon fontSet="material-icons-round">workspace_premium</mat-icon>
    </div>
    <div class="start-banner__body">
      <h4>{{ "backoffice.testModeActive" | transloco }}</h4>
      <p>{{ "backoffice.testModeMessage" | transloco }}</p>
    </div>
    <div class="start-banner__progress">
      <div class="start-banner__bar">
        <div
          class="start-banner__bar-fill"
          [style.width.%]="progressPct()"
        ></div>
      </div>
      <span>{{
        "stats.startProgress" | transloco : { done: completedCount(), total: totalSteps }
      }}</span>
      <app-button
        [text]="'stats.upgrade' | transloco"
        buttonType="primary"
        [isBtnSmall]="true"
        (click)="continueOnboarding(proofInput)"
      ></app-button>
    </div>
  </ng-container>

  <ng-template #setupBanner>
    <div class="start-banner__icon">
      <mat-icon fontSet="material-icons-round">rocket_launch</mat-icon>
    </div>
    <div class="start-banner__body">
      <h4>{{ "stats.startTitle" | transloco }}</h4>
      <div class="start-banner__next">
        <span class="start-banner__next-label">
          {{ "stats.nextStep" | transloco }}
        </span>
        <strong class="start-banner__next-step">{{ nextStepLabel() }}</strong>
      </div>
      <p>{{ startBannerText() }}</p>
    </div>
    <div class="start-banner__progress">
      <div class="start-banner__bar">
        <div
          class="start-banner__bar-fill"
          [style.width.%]="progressPct()"
        ></div>
      </div>
      <span>{{
        "stats.startProgress" | transloco : { done: completedCount(), total: totalSteps }
      }}</span>
      <app-button
        [text]="'stats.continue' | transloco"
        buttonType="secondary"
        [isBtnSmall]="true"
        (click)="continueOnboarding(proofInput)"
      ></app-button>
    </div>
  </ng-template>
  </ng-template>
</div>

<ng-container *ngIf="dashboard()">
  <!-- KPI tiles -->
  <div class="stats">
    <div class="stat" *ngFor="let card of statCards()">
      <div class="stat__label">
        {{ card.labelKey | transloco : card.labelParams }}
      </div>
      <div class="stat__value">
        <app-lomy-points
          *ngIf="card.pointsValue != null; else plainStatValue"
          [value]="card.pointsValue"
        ></app-lomy-points>
        <ng-template #plainStatValue>{{ card.value }}</ng-template>
      </div>
      <div
        class="stat__delta"
        *ngIf="card.delta as delta"
        [ngClass]="
          delta.neutral
            ? 'stat__delta--neutral'
            : delta.up
            ? 'stat__delta--up'
            : 'stat__delta--down'
        "
      >
        <mat-icon *ngIf="!delta.neutral" fontSet="material-icons-round">
          {{ delta.up ? "arrow_upward" : "arrow_downward" }}
        </mat-icon>
        {{ delta.text }}
      </div>
    </div>
  </div>

  <!-- Chart + activity feed -->
  <div class="two-col company-statistics__two-col">
    <div class="card">
      <div class="card__head">
        <div class="card__title">{{ "stats.pointsOverTime" | transloco }}</div>
        <div class="seg">
          <button
            *ngFor="let option of dayOptions"
            type="button"
            [class.is-active]="days() === option"
            (click)="setDays(option)"
          >
            {{ option }}d
          </button>
        </div>
      </div>
      <div class="card__body">
        <div class="company-statistics__chart-wrap" *ngIf="hasSeries()">
          <canvas #pointsChart></canvas>
        </div>
        <div class="empty" *ngIf="!hasSeries()">
          <mat-icon fontSet="material-icons-round">show_chart</mat-icon>
          <h4>{{ "stats.noChartDataTitle" | transloco }}</h4>
          <p>{{ "stats.noChartDataText" | transloco }}</p>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card__head">
        <div class="card__title">{{ "stats.recentActivity" | transloco }}</div>
        <a class="card__link" routerLink="/transactions">
          {{ "stats.viewAll" | transloco }}
        </a>
      </div>
      <div>
        <div class="feed-item" *ngFor="let item of feedItems()">
          <div class="feed-icon" [ngClass]="'feed-icon--' + item.tone">
            <mat-icon fontSet="material-icons-round">{{ item.icon }}</mat-icon>
          </div>
          <div class="feed-body">
            <div class="feed-title">{{ item.title }}</div>
            <div class="feed-sub" *ngIf="item.sub">{{ item.sub }}</div>
          </div>
          <div class="feed-time">{{ item.time }}</div>
        </div>
        <div class="feed-item" *ngIf="feedItems().length === 0">
          <div class="feed-body">
            <div class="feed-sub">{{ "stats.noActivity" | transloco }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<app-spinner [enableSpinner]="loading()"></app-spinner>
<app-no-content-card
  [showContentCard]="dashboardError() && !loading()"
  [absolutePosition]="true"
  [text]="'stats.noContent' | transloco"
></app-no-content-card>
